import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Filter } from 'src/app/models/filter';
import { environment } from 'src/environments/environment';
import { DigitalSignature } from '../models/digital-signature';
import { ContractQuotaFilter } from '../models/filters/contractQuota-filter';
import { LoansFilter } from '../models/filters/loans-filter';
import { OpenDocumentsFilter } from '../models/filters/open-documents-filter';
import { RecollectionFilter } from '../models/filters/recollection-filter';
import { VoidPaymentFilter } from '../models/filters/void-payment-filter';
import { Loan } from '../models/loan';
import { Paging } from '../models/page';
import { Recollection } from '../models/recollection';
import { CashLoanCreateRequest } from '../models/request/cash-loan-create-request';
import { ContractCondonationInfoRequest } from '../models/request/contract-condonation-info-request';
import { ContractReturnRequestCompleteModel } from '../models/request/contract-return-request-complete-model';
import { RecollectionRepairshopRequestModel, RecollectionRequestModel } from '../models/request/recollection-request-model';
import { VoidPaymentRequest } from '../models/request/void-payment-request';
import { LoanSimulationRequest } from '../models/requests/loan-simulation-request';
import { ResponseModel } from '../models/response-model';
import { ContractCondonationInfoResponse } from '../models/response/contract-condonation-info-response';
import { ContractReturnResponseCompleteModel } from '../models/response/contract-return-response-complete-model';
import { ContractStateResponseModel } from '../models/response/contract-state-response-model';
import {
  ContractQuota,
  OpenDocument,
  OpenDocumentsAndQuotaResponse,
} from '../models/response/open-documents-and-quotas-response';
import { TotalToPayoffResponse } from '../models/response/total-payoff-response';
import { VoidPayment } from '../models/void-payment';
import { RecollectionResponse } from './../models/response/RecollectionResponse';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root',
})
export class LoanService {

  private url = `${environment.url}/loans`;
  private returnURL = `${environment.url}/returnContract`;
  private balanceURL = `${environment.url}/contractState`;
  private codonationURL = `${environment.url}/condonation`
  private recollectionURL = `${environment.url}/contractRecollections`;
  private voidUrl = `${environment.url}/loan/void`;
  private settlementValidationURL = `${environment.url}/settlement/dateValidation`;
  constructor(private http: HttpClient, private configService: ConfigurationService) { }

  createCalculateConditionsSimulate(
    parameters: LoanSimulationRequest
  ): Observable<ResponseModel<Loan>> {
    let params = new HttpParams();
    params = params.set('simulate', 'true');
    return this.http.post<ResponseModel<Loan>>(
      `${this.url}/calculateConditions`,
      parameters,
      { params }
    );
  }

  getCustomerLoans(
    filter: Filter<LoansFilter>
  ): Observable<ResponseModel<Paging<Array<Loan>>>> {
    const url = `${environment.url}/loansFranchised`;

    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    params = filter.value.customerCode
      ? params.set('customer', filter.value.customerCode)
      : params;
    params = filter.value.contractNumber
      ? params.set('contractNumber', filter.value.contractNumber)
      : params;
    params = filter.value.startDate
      ? params.set('startDate', filter.value.startDate)
      : params;
    params = filter.value.endDate
      ? params.set('endDate', filter.value.endDate)
      : params;
    params = filter.value.hasContractNumber
      ? params.set('hasContractNumber', 'true')
      : params;
    params = filter.value.invoiceNumber
      ? params.set('invoices', filter.value.invoiceNumber)
      : params;
    params = filter.value.isCashLoan
      ? params.set('isCashLoan', "true")
      : params;
    params = filter.value.branchCode
      ? params.set('branchCode', filter.value.branchCode)
      : params;
    params = filter.value.selectedBranchCode
      ? params.set('selectedBranchCode', filter.value.selectedBranchCode)
      : params;
    params = filter.value.from
      ? params.set('from', filter.value.from)
      : params;
    return this.http.get<ResponseModel<Paging<Array<Loan>>>>(url, { params });
  }



  getActiveLoansByFilters(
    filter: Filter<LoansFilter>
  ): Observable<ResponseModel<Paging<Array<Loan>>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const {
      customerCode,
      contractNumber,
      startDate,
      endDate,
      hasContractNumber,
      invoiceNumber,
      from
    } = filter.value;

    params = customerCode ? params.set('customer', customerCode) : params;
    params = contractNumber
      ? params.set('contractNumber', contractNumber)
      : params;
    params = startDate ? params.set('startDate', startDate) : params;
    params = endDate ? params.set('endDate', endDate) : params;
    params = hasContractNumber ? params.set('hasContractNumber', 'true') : params;
    params = from ? params.set('from', from) : params;
    return this.http.get<ResponseModel<Paging<Array<Loan>>>>(
      `${this.url}/active`,
      {
        params,
      }
    );
  }

  getLoansByFilters(
    filter: Filter<LoansFilter>
  ): Observable<ResponseModel<Array<Loan>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const {
      customerCode,
      contractNumber,
      startDate,
      endDate,
      hasContractNumber,
      invoiceNumber,
    } = filter.value;

    params = customerCode ? params.set('customer', customerCode) : params;
    params = contractNumber
      ? params.set('contractNumber', contractNumber)
      : params;
    params = startDate ? params.set('startDate', startDate) : params;
    params = endDate ? params.set('endDate', endDate) : params;
    params = hasContractNumber
      ? params.set('hasContractNumber', 'true')
      : params;

    return this.http.get<ResponseModel<Array<Loan>>>(
      `${this.url}`,
      {
        params,
      }
    );
  }

  getCurrentCreditsByCustomer(
    customerCode: string
  ): Observable<ResponseModel<Loan[]>> {
    let params = new HttpParams();
    params = params.set('customer', customerCode);

    return this.http.get<ResponseModel<Loan[]>>(`${this.url}`, { params });
  }
  getVoidPayments(filter: Filter<VoidPaymentFilter>): Observable<ResponseModel<Paging<Array<VoidPayment>>>> {
    let params = new HttpParams();
    params = params.set("salesOrg", this.configService.readSelectedBranchAndSalesOrg().salesOrg.code)
      .set("branchCode", this.configService.readSelectedBranchAndSalesOrg().branch.code)
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');;

    params = filter.value.documents ? params.append("documents", filter.value.documents) : params;
    params = filter.value.contracts ? params.append("contracts", filter.value.contracts) : params;
    params = filter.value.customerCode ? params.append("customerCode", filter.value.customerCode) : params;

    return this.http.get<ResponseModel<Paging<Array<VoidPayment>>>>(this.voidUrl, { params });
  }

  getOpenDocumentsAndQuotasFromCustomer(
    customerCode?: string,
    contractNumber?: string,
    salesOrg?: string
  ): Observable<ResponseModel<OpenDocumentsAndQuotaResponse>> {
    let params = new HttpParams();

    if (customerCode != undefined)
      params = params.set('customerCode', customerCode);

    if (contractNumber != undefined)
      params = params.set('contractNumber', contractNumber);

    if (salesOrg != undefined) params = params.set('salesOrg', salesOrg);

    return this.http.get<ResponseModel<OpenDocumentsAndQuotaResponse>>(
      `${this.url}/quotas`,
      { params }
    );
  }

  readContractBalance(salesOrg: string, contractNumber: string) {
    let params = new HttpParams();
    params = params.set('societyContract', salesOrg);
    params = params.set('contract', contractNumber);

    return this.http.get<ResponseModel<ContractStateResponseModel>>(
      this.balanceURL,
      { params }
    );
  }

  createContractReturn(request: ContractReturnRequestCompleteModel) {
    return this.http.post<ResponseModel<ContractReturnResponseCompleteModel>>(
      `${this.returnURL}/complete`,
      request
    );
  }

  getOnTimeQuotas(
    filter: Filter<ContractQuotaFilter>
  ): Observable<ResponseModel<Paging<Array<ContractQuota>>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const { customerCode, contractNumber, salesOrgCode } = filter.value;

    params = customerCode ? params.set('customerCode', customerCode) : params;
    params = contractNumber
      ? params.set('contractNumber', contractNumber)
      : params;
    params = salesOrgCode ? params.set('salesOrg', salesOrgCode) : params;

    return this.http.get<ResponseModel<Paging<Array<ContractQuota>>>>(
      `${this.url}/onTimeQuotas`,
      { params }
    );
  }

  getLateQuotas(
    filter: Filter<ContractQuotaFilter>
  ): Observable<ResponseModel<Paging<Array<ContractQuota>>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const { customerCode, contractNumber, salesOrgCode } = filter.value;

    params = customerCode ? params.set('customerCode', customerCode) : params;
    params = contractNumber
      ? params.set('contractNumber', contractNumber)
      : params;
    params = salesOrgCode ? params.set('salesOrg', salesOrgCode) : params;

    return this.http.get<ResponseModel<Paging<Array<ContractQuota>>>>(
      `${this.url}/lateQuotas`,
      { params }
    );
  }

  getOpenDocuments(
    filter: Filter<OpenDocumentsFilter>
  ): Observable<ResponseModel<Paging<Array<OpenDocument>>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const { customerCode, reference, salesOrgCode, justRepairshopDocuments, document, amount } = filter.value;

    params = customerCode ? params.set('customerCode', customerCode) : params;
    params = reference
      ? params.set('reference', reference)
      : params;
    params = salesOrgCode ? params.set('salesOrg', salesOrgCode) : params;
    params = justRepairshopDocuments ? params.set('justRepairshopDocuments', `${justRepairshopDocuments}`) : params;
    params = document ? params.set('document', document) : params;
    params = amount ? params.set('amount', amount) : params;

    return this.http.get<ResponseModel<Paging<Array<OpenDocument>>>>(
      `${this.url}/openDocuments`,
      { params }
    );
  }

  makeRecollection(body: RecollectionRequestModel): Observable<ResponseModel<RecollectionResponse>> {
    return this.http.post<ResponseModel<RecollectionResponse>>(`${this.url}/createRecollection/quotas`, body);
  }

  getTotalToPayOffByContractNumber(contractNumber: string): Observable<ResponseModel<TotalToPayoffResponse>> {
    let params: HttpParams = new HttpParams();
    params = params.set('contractNumber', contractNumber);
    return this.http.get<ResponseModel<TotalToPayoffResponse>>(`${this.url}/createRecollection/totalPayOff`, { params });
  }

  makeRecollectionPayoff(body: RecollectionRequestModel): Observable<ResponseModel<RecollectionResponse>> {
    return this.http.post<ResponseModel<RecollectionResponse>>(`${this.url}/createRecollection/payOff`, body);
  }

  getContractCondonationInfo(condonationInfoRequest: ContractCondonationInfoRequest): Observable<ResponseModel<Array<ContractCondonationInfoResponse>>> {
    return this.http.post<ResponseModel<Array<ContractCondonationInfoResponse>>>(this.codonationURL, condonationInfoRequest);
  }

  makeRecollectionCondonation(body: RecollectionRequestModel): Observable<ResponseModel<RecollectionResponse>> {
    return this.http.post<ResponseModel<RecollectionResponse>>(`${this.url}/createRecollection/condonation`, body);
  }

  makeRepairshopInvoiceRecollection(body: RecollectionRepairshopRequestModel): Observable<ResponseModel<RecollectionResponse>> {
    return this.http.post<ResponseModel<RecollectionResponse>>(`${this.url}/createRecollection/repairshopInvoice`, body);
  }

  makeOpenDocumentsRecollection(body: RecollectionRepairshopRequestModel): Observable<ResponseModel<RecollectionResponse>> {
    return this.http.post<ResponseModel<RecollectionResponse>>(`${this.url}/createRecollection/openDocuments`, body);
  }

  getRecollectionByCode(code: string): Observable<ResponseModel<Recollection>> {
    return this.http.get<ResponseModel<Recollection>>(`${this.recollectionURL}/${code}`);
  }

  createVoidPayment(request: VoidPaymentRequest) {
    return this.http.post<ResponseModel<{}>>(this.voidUrl, request);
  }

  getCanceledLoansByFilters(
    filter: Filter<LoansFilter>
  ): Observable<ResponseModel<Paging<Array<Loan>>>> {
    let params = new HttpParams();
    params = params
      .set('page', filter.page.toString() || '0')
      .set('pageSize', filter.pageSize.toString() || '20');

    const {
      customerCode,
      contractNumber,
      startDate,
      endDate,
      hasContractNumber,
      invoiceNumber,
    } = filter.value;

    params = customerCode ? params.set('customer', customerCode) : params;
    params = contractNumber
      ? params.set('contractNumber', contractNumber)
      : params;
    params = startDate ? params.set('startDate', startDate) : params;
    params = endDate ? params.set('endDate', endDate) : params;
    params = hasContractNumber
      ? params.set('hasContractNumber', 'true')
      : params;

    return this.http.get<ResponseModel<Paging<Array<Loan>>>>(
      `${this.url}/canceled`,
      {
        params,
      }
    );
  }

  getRecollectionListByFilter(filter: Filter<RecollectionFilter>): Observable<ResponseModel<Paging<Array<Recollection>>>> {

    let params = new HttpParams();

    const { customerCode, code, status, createdBy, createdOn, branchCode, salesOrg } = filter.value;

    params = customerCode ? params.append('customerCode', customerCode) : params;
    params = code ? params.append('code', code) : params;
    params = status ? params.append('status', status) : params;
    params = createdBy ? params.append('createdBy', createdBy) : params;
    params = createdOn ? params.append('createdOn', `${createdOn}`) : params;
    params = branchCode ? params.append('branchCode', branchCode) : params;
    params = salesOrg ? params.append('salesOrg', salesOrg) : params;
    params = params.append('page', `${filter.page}`);
    params = params.append('pageSize', `${filter.pageSize}`);

    return this.http.get<ResponseModel<Paging<Array<Recollection>>>>(`${this.recollectionURL}`, { params });

  }

  settlementDateValidation(contractNumber: string) {
    let params = new HttpParams();

    params = params.set("contractNumber", contractNumber);
    return this.http
      .get<ResponseModel<boolean>>(this.settlementValidationURL, { params });
  }

  readLoan(code: string) {
    let params = new HttpParams();
    return this.http
      .get<ResponseModel<Loan>>(`${environment.url}/loan/${code}`, { params });
  }

  createCashLoan(request: CashLoanCreateRequest) {
    return this.http.post<ResponseModel<Loan>>(`${environment.url}/loan/cash/create`, request);
  }

  updatePrintAmount(contractNumber: string) {
    return this.http
      .put<ResponseModel<Loan>>(`${environment.url}/loan/${contractNumber}/updateReprintInvoices`, {});
  }

  readDigitalSignature(contractCode: string) {
    return this.http.get<ResponseModel<DigitalSignature>>(`${environment.url}/loans/${contractCode}/digitalSignature`);
  }

}
